import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators, UntypedFormArray } from '@angular/forms';
import { mTypeRateProfile, RatesProfile, RatesProfileNew } from '@models/superaccount/rates-profile';
import { SuperaccountService } from '@services/superaccount.service';
import * as _ from 'lodash';

import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { MessageType } from '@models/sms/messagetype';
import { AtlasToastService } from '@services/atlas-toast.service';
import { SmsService } from '@services/sms.service';
import { ToastContainerDirective } from 'ngx-toastr';
import { nations } from '@utils/constants';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { UserService } from '@services/user.service';
import { User } from '@models/user';
import { TranslateService } from '@ngx-translate/core';
import { PrefixLabels } from '@services/subaccount.service';

// custom validator for not accepting 0 e 0.0,0.00,0.000
function notPriceValidator(): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const price = control.value;
    const notPrice = [0, 0.0, 0.0, 0.0];
    if (notPrice.includes(price)) {
      return { pattern: true };
    }
    return null;
  };
}

@Component({
  selector: 'wsb-reseller-price-edit',
  templateUrl: './reseller-price-edit.component.html',
  styleUrls: ['./reseller-price-edit.component.scss'],
  standalone: false
})
export class ResellerPriceEditComponent implements OnInit {
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;

  editForm: UntypedFormGroup = null;
  nations = [];
  // dto
  rateProfileToEdit?: RatesProfile;
  messageType: MessageType;
  faTrash = faTrash;
  user: User | null;

  // checkbox
  isCheckedGpPrice: boolean;
  isCheckedGsPrice: boolean;
  isCheckedSiPrice: boolean;
  isCheckedTiPrice: boolean;
  isCheckedAdPrice: boolean;
  isCheckedEePrice: boolean;
  isCheckedIndexPrice = {};
  // reference price per message component
  gp = mTypeRateProfile.GP;
  gs = mTypeRateProfile.GS;
  ti = mTypeRateProfile.TI;
  si = mTypeRateProfile.SI;
  adv = mTypeRateProfile.ADV;
  ee = mTypeRateProfile.EE;

  // id editing
  private mapMassageType = new Map<string, string>();

  // pattern for numeric field:
  private pricePattern: RegExp = /^[0-9]\d*(.\d{1,4})?$/;

  // main page rate profile;
  private urlMainPage = '/reseller/prices';

  // test-only
  // test =["GP","GS","TI","SI","ADV"];

  constructor(
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    private activateRoute: ActivatedRoute,
    private router: Router,
    private superaccountService: SuperaccountService,
    private atlasToast: AtlasToastService,
    private location: Location,
    private smsService: SmsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.userService.getAuthenticationState().subscribe(next => (this.user = next));

    nations.forEach(x => {
      this.nations.push({
        id: x[0],
        label: x[1]
      });
    });

    this.editForm = this.formBuilder.group({
      idRateProfile: [, []],
      name: [, [Validators.required]],
      defaultAmount: [, [Validators.required, Validators.pattern(this.pricePattern), notPriceValidator()]],
      pricePerMessage: this.formBuilder.array([])
    });

    this.smsService.getMessageTypes().subscribe(response => {
      response.data.forEach(mt => this.mapMassageType.set(mt.id.toUpperCase(), mt.id.toUpperCase()));
    });

    // only-test
    // this.test.forEach(mt => this.mapMassageType.set(mt.toUpperCase(),mt.toUpperCase()));

    if (!_.isNull(this.activateRoute.snapshot.paramMap.get('id'))) {
      const idRateprofile = this.activateRoute.snapshot.paramMap.get('id');
      this.superaccountService.loadRateProfileNew(idRateprofile).subscribe(data => {
        this.rateProfileToEdit = RatesProfileNew.deserialize(data);
        // this.setCheckboxPricePerMessage(data);
        this.editForm.patchValue(data);
        this.editForm.markAsDirty();
        data.pricePerMessage.forEach(element => {
          this.pricePerMessage.push(
            this.formBuilder.group({
              idDestinationNation: [element.idDestinationNation],
              type: [element.type],
              cost: [element.cost, [Validators.pattern(this.pricePattern), notPriceValidator()]]
            })
          );
          this.isCheckedIndexPrice[element.idDestinationNation + '_' + element.type] = true;
        });
        this.createDefaultPriceFormControl();
        this.sortPricePerNation();
        this.editForm.markAsPristine();
      });
    } else {
      this.createDefaultPriceFormControl();
      this.sortPricePerNation();
    }
  }

  sortPricePerNation() {
    const nationUser = this.user.nation;
    let sortedProducts3 = this.editForm.get('pricePerMessage').value.sort(function(a, b) {
      if (a.type === 'EE') {
        return 1;
      }
      if (b.type === 'EE') {
        return -1;
      }
      if (!a.idDestinationNation) {
        return -1;
      } else if (a.idDestinationNation === nationUser && b.idDestinationNation) {
        return -1;
      }
      if (!b.idDestinationNation) {
        return 1;
      } else if (b.idDestinationNation === nationUser && a.idDestinationNation) {
        return 1;
      }
      if (a.idDestinationNation > b.idDestinationNation) {
        return 1;
      }
      if (a.idDestinationNation < b.idDestinationNation) {
        return -1;
      }
      return 0;
    });
    this.editForm.get('pricePerMessage').patchValue(sortedProducts3);
  }

  removePricePerNation(index) {
    this.pricePerMessage.removeAt(index);
    this.sortPricePerNation();
    this.editForm.markAsDirty();
  }

  createDefaultPriceFormControl() {
    Object.keys(mTypeRateProfile).forEach(type => {
      if (type !== 'GP') {
        const find = _.find(this.pricePerMessage.value, { type: type, idDestinationNation: null });
        if (!find) {
          this.pricePerMessage.push(
            this.formBuilder.group({
              idDestinationNation: [null],
              type: [type],
              cost: ['', [Validators.pattern(this.pricePattern), notPriceValidator()]]
            })
          );
        }
      } else {
        const find = _.find(this.pricePerMessage.value, { type: type, idDestinationNation: this.user.nation });
        if (!find) {
          this.pricePerMessage.push(
            this.formBuilder.group({
              idDestinationNation: [this.user.nation],
              type: [type],
              cost: ['', [Validators.pattern(this.pricePattern), notPriceValidator()]]
            })
          );
        }
      }
    });
  }

  get pricePerMessage(): UntypedFormArray {
    return this.editForm.get('pricePerMessage') as UntypedFormArray;
  }

  selectNation(ev) {
    const isExist = _.find(this.pricePerMessage.value, { idDestinationNation: ev.id });
    if (!isExist) {
      this.pricePerMessage.push(
        this.formBuilder.group({
          idDestinationNation: [ev.id],
          type: ['GP'],
          cost: ['', [Validators.pattern(this.pricePattern), notPriceValidator()]]
        })
      );
      this.sortPricePerNation();
    }
  }

  // buttom save
  submitForm() {
    this.editForm.disable();
    const raw: RatesProfile = this.editForm.value;
    raw.pricePerMessage = this.filterPricePerMessage();
    const pricePerMessagePatch = this.buildPatchRequest(raw.pricePerMessage);
    if (this.rateProfileToEdit) {
      delete this.rateProfileToEdit.pricePerMessage;
      delete raw.pricePerMessage;
    }
    const op = this.rateProfileToEdit
      ? this.superaccountService.editRateProfile(this.rateProfileToEdit.idRateProfile, this.rateProfileToEdit, raw, pricePerMessagePatch)
      : this.superaccountService.createRateProfile(raw);
    op.subscribe(
      success => {
        this.atlasToast.success('common_success', null);
        this.router.navigate([this.urlMainPage]);
      },
      error => {
        // console.log(error);
        this.atlasToast.error(
          'common_error',
          error && error.error && error.error.error_message ? error.error.error_message : null,
          6000,
          this.toastContainer
        );
        this.editForm.enable();
      },
      () => {}
    );
  }

  // set checkbox for updating rate profile
  setCheckboxPricePerMessage(data: RatesProfile) {
    this.isCheckedGpPrice = data.pricePerMessage.gpPrice ? true : false;
    this.isCheckedGsPrice = data.pricePerMessage.gsPrice ? true : false;
    this.isCheckedTiPrice = data.pricePerMessage.tiPrice ? true : false;
    this.isCheckedSiPrice = data.pricePerMessage.siPrice ? true : false;
    this.isCheckedAdPrice = data.pricePerMessage.adPrice ? true : false;
    this.isCheckedEePrice = data.pricePerMessage.eePrice ? true : false;
  }

  enableSigleFormPricePerMessage(value: string): void {
    if (value === this.gp) {
      this.verifyEnablePricePerMessage(this.isCheckedGpPrice, 'pricePerMessage.gpPrice');
    } else if (value === this.gs) {
      this.verifyEnablePricePerMessage(this.isCheckedGsPrice, 'pricePerMessage.gsPrice');
    } else if (value === this.ti) {
      this.verifyEnablePricePerMessage(this.isCheckedTiPrice, 'pricePerMessage.tiPrice');
    } else if (value === this.si) {
      this.verifyEnablePricePerMessage(this.isCheckedSiPrice, 'pricePerMessage.siPrice');
    } else if (value === this.adv) {
      this.verifyEnablePricePerMessage(this.isCheckedAdPrice, 'pricePerMessage.adPrice');
    } else if (value === this.ee) {
      this.verifyEnablePricePerMessage(this.isCheckedEePrice, 'pricePerMessage.eePrice');
    }
  }

  private verifyEnablePricePerMessage(isCheckedPrice: boolean, formName: string) {
    if (!isCheckedPrice) {
      this.editForm.get(formName).setValue(null);
      this.editForm.get(formName).clearValidators();
    } else {
      this.editForm.get(formName).setValidators([Validators.pattern(this.pricePattern), notPriceValidator()]);
    }
    this.editForm.get(formName).updateValueAndValidity();
    this.editForm.markAsDirty();
  }

  isReadOnly(form) {
    if (this.isCheckedIndexPrice[form.value.idDestinationNation + '_' + form.value.type]) {
      return false;
    }
    return true;
  }

  setCheckedPrice(form) {
    this.isCheckedIndexPrice[form.value.idDestinationNation + '_' + form.value.type] = !this.isCheckedIndexPrice[
      form.value.idDestinationNation + '_' + form.value.type
    ];
  }

  resetForm() {
    this.editForm.reset();
  }

  goBack() {
    this.location.back();
  }

  isMessageType(type: string): boolean {
    if (type === 'EE') {
      return true;
    }
    // return true;
    return this.mapMassageType.get(type) ? true : false;
  }

  isNotSelectedCheckBox(): boolean {
    if (
      !this.isCheckedGpPrice &&
      !this.isCheckedGsPrice &&
      !this.isCheckedTiPrice &&
      !this.isCheckedSiPrice &&
      !this.isCheckedAdPrice &&
      !this.isCheckedEePrice
    ) {
      return true;
    } else {
      return false;
    }
  }

  filterPricePerMessage() {
    return this.pricePerMessage.value.filter(element => element.cost);
  }
  buildPatchRequest(data) {
    const patch = { op: 'replace', path: '/pricePerMessage', value: data };
    return patch;
  }

  findLabelNation(id) {
    const nation = _.find(this.nations, { id: id });
    if (nation) {
      if (id === this.user.nation) {
        return this.translateService.instant('credit_type_gp');
      }
      return nation.label;
    }
    return id + ' - unknow';
  }

  findLabelTypeNation(type, idNation) {
    if (!(idNation && type === 'gp' && idNation !== this.user.nation)) {
      return this.getLabelsTranslate(PrefixLabels.CREDIT_TYPE + type);
    } else {
      return this.findLabelNation(idNation);
    }
  }

  private getLabelsTranslate(label: string) {
    let labels;
    this.translateService.get(label).subscribe(l => {
      labels = l;
    });
    return labels;
  }
}
